/**
 * Dialog para gerenciar (criar/editar/excluir) tags do tenant.
 *
 * v3.0.0 - 2026-08-29 - CRUD 100% server-side:
 *   - Removidos os acessos diretos à tabela contact_tags
 *   - Passa a usar o hook centralizado useContactTags (RPCs seguras)
 *   - Logs padronizados via src/lib/logger.ts
 *   - UI, validações e permissões preservadas
 *
 * v2.0.0 - 2026-06-03 - Redesign visual "Dark Executive":
 *   - Header refinado com título + descrição e botão X custom
 *   - Barra de busca filtrando tags em tempo real
 *   - Lista de tags com pílulas coloridas e ações inline
 *   - Formulário de criar/editar em card destacado com presets de cor
 *   - Footer fixo com botões primários
 *   - 100% baseado em tokens semânticos do design system (light/dark)
 *   - Lógica de CRUD, validações e permissões preservadas
 */


import { useState, useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Search,
  Tag as TagIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import {
  useContactTags,
  useCreateContactTag,
  useUpdateContactTag,
  useDeleteContactTag,
} from '@/hooks/useContactTags';

// Logger condicional por ambiente (padrão do projeto)
const logger = createLogger('TagManagementDialog');


// Schema de validação da tag
const tagSchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório').max(50, 'Nome muito longo'),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Cor inválida'),
});

type TagFormData = z.infer<typeof tagSchema>;

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface TagManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  onSuccess: () => void;
  // Permissões granulares (default true para compatibilidade)
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

// Cores predefinidas para seleção rápida
const PRESET_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#0ea5e9', // Sky
  '#3b82f6', // Blue
];

export function TagManagementDialog({
  open,
  onOpenChange,
  tenantId,
  onSuccess,
  canCreate = true,
  canEdit = true,
  canDelete = true,
}: TagManagementDialogProps) {
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fonte única de dados: hook centralizado (RPCs server-side)
  const { data: tags = [], isLoading: loading } = useContactTags(tenantId, open);
  const createTag = useCreateContactTag();
  const updateTag = useUpdateContactTag();
  const deleteTag = useDeleteContactTag();

  // Indica operação de escrita em andamento (criar/editar)
  const saving = createTag.isPending || updateTag.isPending;

  const form = useForm<TagFormData>({
    resolver: zodResolver(tagSchema),
    defaultValues: {
      name: '',
      color: PRESET_COLORS[0],
    },
  });

  // Limpa a busca ao abrir o dialog (a listagem é carregada pelo hook)
  useEffect(() => {
    if (open) {
      setSearchTerm('');
    }
  }, [open, tenantId]);

  // Inicia criação de nova tag
  const handleCreate = () => {
    setEditingTag(null);
    setIsCreating(true);
    form.reset({
      name: '',
      color: PRESET_COLORS[Math.floor(Math.random() * PRESET_COLORS.length)],
    });
  };

  // Inicia edição de tag existente
  const handleEdit = (tag: Tag) => {
    setIsCreating(false);
    setEditingTag(tag);
    form.reset({
      name: tag.name,
      color: tag.color,
    });
  };

  // Cancela criação/edição
  const handleCancel = () => {
    setEditingTag(null);
    setIsCreating(false);
    form.reset();
  };

  // Exclui tag (RPC delete_contact_tag: remove vínculos na mesma transação)
  const handleDelete = async (tag: Tag) => {
    try {
      await deleteTag.mutateAsync({ tagId: tag.id, tenantId });
      toast.success('Tag excluída');
      onSuccess();
    } catch (error: any) {
      logger.error('Erro ao excluir tag', error);
      toast.error(error?.message || 'Erro ao excluir tag');
    }
  };

  // Submete criação ou edição via RPCs seguras
  const onSubmit = async (data: TagFormData) => {
    try {
      if (editingTag) {
        await updateTag.mutateAsync({
          tagId: editingTag.id,
          name: data.name,
          color: data.color,
          tenantId,
        });
        toast.success('Tag atualizada');
      } else {
        await createTag.mutateAsync({
          name: data.name,
          color: data.color,
          tenantId,
        });
        toast.success('Tag criada');
      }

      handleCancel();
      onSuccess();
    } catch (error: any) {
      logger.error('Erro ao salvar tag', error);
      // Mensagens de negócio vêm prontas do banco (RAISE EXCEPTION)
      toast.error(error?.message || 'Erro ao salvar tag');
    }
  };


  // Filtra tags por termo de busca (case-insensitive)
  const filteredTags = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return tags;
    return tags.filter((t) => t.name.toLowerCase().includes(term));
  }, [tags, searchTerm]);

  const isFormOpen = isCreating || !!editingTag;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px] p-0 gap-0 overflow-hidden"
        hideCloseButton
      >
        {/* ============== HEADER ============== */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-border/60">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-foreground tracking-tight">
              Gerenciar Tags
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Crie e organize tags para segmentar seus contatos.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground shrink-0"
            onClick={() => onOpenChange(false)}
            title="Fechar"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* ============== SEARCH ============== */}
        {!loading && tags.length > 0 && !isFormOpen && (
          <div className="px-6 pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar tag..."
                className="pl-9 h-10 bg-muted/40 border-border/60 focus-visible:ring-ring/30"
              />
            </div>
          </div>
        )}

        {/* ============== CONTEÚDO ============== */}
        <div className="px-6 py-4 overflow-y-auto max-h-[440px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* ===== Formulário de criar/editar ===== */}
              {isFormOpen && (
                <div className="rounded-lg border border-border/60 bg-muted/40 p-4 space-y-3">
                  <h3 className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">
                    {editingTag ? 'Editando tag' : 'Nova tag'}
                  </h3>
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmit)}
                      className="space-y-3"
                    >
                      <div className="flex items-center gap-2">
                        <FormField
                          control={form.control}
                          name="color"
                          render={({ field }) => (
                            <FormItem className="space-y-0">
                              <FormControl>
                                <input
                                  type="color"
                                  {...field}
                                  className="w-9 h-9 rounded-md cursor-pointer border border-border/60 bg-transparent"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem className="flex-1 space-y-0">
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="Nome da tag"
                                  className="h-9"
                                  autoFocus
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button type="submit" size="sm" disabled={saving} className="h-9">
                          {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={handleCancel}
                          className="h-9"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Cores rápidas */}
                      <div className="space-y-2">
                        <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">
                          Cores rápidas
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {PRESET_COLORS.map((color) => {
                            const isActive = form.watch('color') === color;
                            return (
                              <button
                                key={color}
                                type="button"
                                className={cn(
                                  'w-7 h-7 rounded-full transition-transform hover:scale-110',
                                  isActive && 'ring-2 ring-offset-2 ring-offset-background'
                                )}
                                style={{
                                  backgroundColor: color,
                                  // Usa a própria cor como anel quando ativo
                                  ['--tw-ring-color' as any]: color,
                                }}
                                onClick={() => form.setValue('color', color)}
                                aria-label={`Selecionar cor ${color}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </form>
                  </Form>
                </div>
              )}

              {/* ===== Lista de tags ===== */}
              {tags.length === 0 ? (
                <div className="text-center py-10 space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <TagIcon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Nenhuma tag criada ainda.
                  </p>
                </div>
              ) : (
                <div>
                  <h3 className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-2">
                    Tags · {tags.length}
                  </h3>
                  {filteredTags.length === 0 ? (
                    <div className="text-center py-6 text-sm text-muted-foreground">
                      Nenhuma tag corresponde a "{searchTerm}".
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {filteredTags.map((tag) => (
                        <div
                          key={tag.id}
                          className="flex items-center justify-between p-2.5 rounded-lg border border-transparent hover:bg-muted/40 hover:border-border/60 transition-all"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: tag.color }}
                            />
                            <span
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border"
                              style={{
                                backgroundColor: `${tag.color}15`,
                                borderColor: `${tag.color}40`,
                                color: tag.color,
                              }}
                            >
                              {tag.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                onClick={() => handleEdit(tag)}
                                title="Editar"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                onClick={() => handleDelete(tag)}
                                title="Excluir"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ============== FOOTER ============== */}
        <div className="px-6 py-4 bg-muted/30 border-t border-border/60 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Fechar
          </Button>
          {!isFormOpen && canCreate && (
            <Button onClick={handleCreate} className="shadow-sm">
              <Plus className="h-4 w-4 mr-2" />
              Nova Tag
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
